import { LOGIN_PATH } from '@/common/main/config';
import { isEmbeddedSession, withEmbedQueryIfNeeded } from '@/common/application/embeddedMode';
import { GraphQLError } from 'graphql';
import type { AuthService } from '@/auth/application/AuthService';
import type { AxiosError } from 'axios';

/**
 * Checks if an error is an authentication error
 * @param error The error to check
 * @returns boolean indicating if the error is an auth error
 */
export const isAuthError = (error: unknown): boolean => {
  // Handle null/undefined errors
  if (!error) {
    return false;
  }

  // Check for GraphQL errors
  if (error instanceof GraphQLError) {
    const extensions = error.extensions;

    // Special handling for 403 GraphQL errors - check if it's a file operation
    if (extensions?.statusCode === 403 || extensions?.errorType === 403) {
      return isFileOperationGraphQL403Error(error);
    }

    return (
      extensions?.code === 'AUTH_NOT_AUTHORIZED' ||
      extensions?.code === 'UNAUTHORIZED' ||
      extensions?.statusCode === '401' ||
      (typeof error.message === 'string' &&
        (error.message.toLowerCase().includes('not authorized') || error.message.toLowerCase().includes('unauthorized')))
    );
  }

  // Check for Axios errors
  if (isAxiosError(error)) {
    // User Management is a JWT resource server: 403 means forbidden for the action/resource,
    // not an expired session. Logging out on UM 403 (e.g. Users grid page-size refetch) is a false positive (TROC-312).
    const requestUrl = error.config?.url ?? '';
    if (requestUrl.includes('/api/user-management/') && error.response?.status === 403) {
      return false;
    }

    if (requestUrl.includes('/auth/exchange-from-erp') || requestUrl.includes('/self-serve-provision/public')) {
      return false;
    }

    // Special handling for 403 errors - check if it's a file operation
    if (error.response?.status === 403) {
      return isFileOperation403Error(error);
    }

    return error.response?.status !== undefined && [401, 419].includes(error.response.status);
  }

  // Check for error objects with similar structure
  if (isErrorWithExtensions(error)) {
    return !!(
      error.extensions?.code === 'AUTH_NOT_AUTHORIZED' ||
      error.extensions?.code === 'UNAUTHORIZED' ||
      error.extensions?.statusCode === '401' ||
      (error.message && (error.message.includes('not authorized') || error.message.includes('unauthorized')))
    );
  }

  // Check for plain error objects with auth-related messages
  if (isErrorWithMessage(error)) {
    return (
      error.message.toLowerCase().includes('not authorized') ||
      error.message.toLowerCase().includes('unauthorized') ||
      error.message.toLowerCase().includes('authentication') ||
      error.message.toLowerCase().includes('auth')
    );
  }

  return false;
};

/**
 * Checks if a 403 error is related to file operations that should not trigger logout
 * @param error The error to check
 * @returns boolean indicating if this is a file operation 403 error
 */
const isFileOperation403Error = (error: unknown): boolean => {
  if (!isAxiosError(error) || !error.response) {
    return false;
  }

  const url = error.config?.url || '';
  const method = error.config?.method || '';
  const data = error.config?.data;

  // Check if payload has filesToRemove (indicates file deletion operation)
  const hasFilesToRemove = data instanceof FormData ? data.has('filesToRemove') : false;

  // Check if this is a file operation endpoint
  const isFileEndpoint =
    url.includes('/api/files') ||
    (url.includes('/api/v3/permits/') && url.includes('/assign/')) ||
    (url.includes('/api/v3/certificates/') && url.includes('/assign/'));

  // Check if this is one of the specific file operations we want to handle differently
  const isFileOperation =
    (method === 'delete' && (url.includes('/api/files/') || url.endsWith('/api/files'))) ||
    (method === 'put' && url.includes('/api/files/')) ||
    (method === 'post' && url.includes('/api/files/permissions')) ||
    (method === 'put' && url.includes('/api/v3/permits/') && url.includes('/assign/') && hasFilesToRemove) ||
    (method === 'put' && url.includes('/api/v3/certificates/') && url.includes('/assign/') && hasFilesToRemove);

  // Only treat as auth error if it's NOT a file operation
  return !(isFileEndpoint && isFileOperation);
};

/**
 * Checks if a 403 GraphQL error is related to file operations that should not trigger logout
 * @param error The GraphQL error to check
 * @returns boolean indicating if this is a file operation 403 error
 */
const isFileOperationGraphQL403Error = (error: GraphQLError): boolean => {
  // Check if the error message contains file-related keywords
  const message = error.message?.toLowerCase() || '';
  const isFileRelated =
    message.includes('file') ||
    message.includes('attachment') ||
    message.includes('permission') ||
    message.includes('delete') ||
    message.includes('update');

  // Check if the error path indicates a file operation
  const path = error.path || [];
  const isFilePath = path.some(
    (segment: unknown) =>
      typeof segment === 'string' && (segment.toLowerCase().includes('file') || segment.toLowerCase().includes('attachment'))
  );

  // Only treat as auth error if it's NOT file-related
  return !(isFileRelated || isFilePath);
};

/**
 * Handles authentication errors by redirecting to login
 * @param authService The auth service instance
 */

let isRedirectingToLogin = false;
export const handleAuthError = (authService: AuthService): void => {
  if (isRedirectingToLogin) return;

  // Check if we're in a browser environment before accessing window
  if (typeof window === 'undefined') {
    return;
  }

  if (!window.location.href.includes(LOGIN_PATH)) {
    isRedirectingToLogin = true;
    // This is needed to avoid the infinite loop of redirects
    // e.g. if the user is not logged in and tries to access a protected route (dashboard page)
    // The user will be redirected to the login page, but the login page will also try to access the dashboard page
    // and this will cause an infinite loop of redirects.
    // By logging out, the user will be logged out and the login page will be displayed.
    authService.logout();
    // Preserve ERP embed chrome-hide (TROC-317). Assigning only pathname drops ?embed=1.
    const embedded = isEmbeddedSession(window.location.search || '');
    const loginTarget = withEmbedQueryIfNeeded(LOGIN_PATH, embedded);
    const loc = window.location as Location & { assign?: (url: string) => void };
    if (typeof loc.assign === 'function') {
      loc.assign(loginTarget);
    } else {
      // Unit-test Location stubs often omit assign — keep pathname assertion compatible.
      loc.pathname = LOGIN_PATH;
      try {
        loc.href = loginTarget;
      } catch {
        // ignore readonly stubs
      }
    }
  }
};

// Test-only utility to reset redirection flag between tests
// Exported to ensure isolated tests that trigger handleAuthError
export const __resetAuthRedirectFlagForTests = (): void => {
  isRedirectingToLogin = false;
};

// ---- Claim URIs per requirements ----
export const CLAIM_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
export const CLAIM_ROLE = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
export const CLAIM_DB_ALIAS = 'DbAlias';
export const CLAIM_EMAIL = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';

// ---- Helpers: ensure token is string and decode base64url safely ----
export function coerceTokenToString(token: unknown): string {
  if (typeof token === 'string') return token;
  if (token instanceof Uint8Array) {
    return typeof TextDecoder !== 'undefined' ? new TextDecoder().decode(token) : String.fromCharCode(...Array.from(token));
  }
  if (Array.isArray(token) && token.every((n) => typeof n === 'number')) {
    const u8 = new Uint8Array(token as number[]);
    return typeof TextDecoder !== 'undefined' ? new TextDecoder().decode(u8) : String.fromCharCode(...Array.from(u8));
  }
  return '';
}

export function base64UrlToUtf8(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    const binary = window.atob(padded);
    try {
      // Convert binary → UTF-8
      return decodeURIComponent(Array.from(binary, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''));
    } catch {
      return binary; // fallback if not UTF-8
    }
  }
  // Node/browser polyfill path

  return Buffer.from(padded, 'base64').toString('utf-8');
}

export function decodeJwt<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    const payloadSeg = parts[1];
    if (!payloadSeg) return null;
    const json = base64UrlToUtf8(payloadSeg);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * Normalize a raw name string to "First Last".
 * - "Last, First [Middle]" -> "First [Middle] Last"
 * - "Last First" (exactly two tokens, no comma) -> "First Last"
 * - Otherwise returns input trimmed
 */
export function normalizeFullName(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim().replace(/\s+/g, ' ');
  const commaIdx = s.indexOf(',');
  if (commaIdx !== -1) {
    const last = s.slice(0, commaIdx).trim();
    const firstAndMiddles = s.slice(commaIdx + 1).trim();
    return [firstAndMiddles, last].filter(Boolean).join(' ');
  }
  const parts = s.split(' ');
  if (parts.length === 2) {
    return `${parts[1]} ${parts[0]}`;
  }
  return s;
}

/**
 * Parses firstName and lastName from a full name string
 * @param fullName - Full name string (e.g., "John Doe" or "Doe, John")
 * @returns Object with firstName and lastName, or null if parsing fails
 */
export function parseNameParts(fullName: string | null | undefined): { firstName: string; lastName: string } | null {
  if (!fullName) return null;
  const normalized = normalizeFullName(fullName);
  if (!normalized) return null;

  const parts = normalized.trim().split(/\s+/);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return { firstName: parts[0]!, lastName: '' };
  }

  // First part is firstName, last part is lastName, middle parts go to firstName
  const firstName = parts.slice(0, -1).join(' ');
  const lastName = parts[parts.length - 1]!;

  return { firstName, lastName };
}

type NodeCryptoModule = typeof import('node:crypto');

declare const require: NodeJS.Require | undefined;

const resolveNodeCrypto = (): NodeCryptoModule | undefined => {
  if (typeof process === 'undefined' || !process.versions?.node) {
    return undefined;
  }

  try {
    if (typeof require === 'undefined') {
      return undefined;
    }
    return require('node:crypto') as NodeCryptoModule;
  } catch {
    return undefined;
  }
};

const nodeCrypto = resolveNodeCrypto();

const formatBytesAsUuid = (bytes: ArrayLike<number>): string => {
  const byteArray = Array.from(bytes);

  if (byteArray.length < 16) {
    throw new Error('Insufficient random bytes for UUID generation.');
  }

  // Per RFC 4122 section 4.4
  byteArray[6] = ((byteArray[6] ?? 0) & 0x0f) | 0x40;
  byteArray[8] = ((byteArray[8] ?? 0) & 0x3f) | 0x80;

  const hex = byteArray.map((byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join('')
  ].join('-');
};

// Type guards
const isAxiosError = (error: unknown): error is AxiosError => {
  return typeof error === 'object' && error !== null && 'response' in error && 'config' in error;
};

const isErrorWithExtensions = (
  error: unknown
): error is { extensions?: { code?: string; statusCode?: string | number }; message?: string } => {
  return typeof error === 'object' && error !== null && 'extensions' in error;
};

const isErrorWithMessage = (error: unknown): error is { message: string } => {
  return typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message?: unknown }).message === 'string';
};

export const generateRegistrationId = (): string => {
  const webCrypto: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
  const cryptoSource = webCrypto ?? nodeCrypto?.webcrypto;

  if (cryptoSource?.randomUUID) {
    return cryptoSource.randomUUID();
  }

  if (typeof nodeCrypto?.randomUUID === 'function') {
    return nodeCrypto.randomUUID();
  }

  if (typeof nodeCrypto?.randomBytes === 'function') {
    const bytes = nodeCrypto.randomBytes(16);
    return formatBytesAsUuid(bytes);
  }

  if (cryptoSource?.getRandomValues) {
    const bytes = new Uint8Array(16);
    const getRandomValues = cryptoSource.getRandomValues as (array: Uint8Array) => Uint8Array;
    getRandomValues(bytes);
    return formatBytesAsUuid(bytes);
  }

  throw new Error('Secure random generator is unavailable in the current runtime.');
};
